import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Globe, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, fmtDate, errMsg } from "@/lib/api";
import { PageTitle } from "@/components/Badges";
import { useAuth } from "@/lib/auth";

const norm = (x) => (x || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/[^A-Z0-9 ]/g, " ").split(" ").filter(Boolean).sort().join(" ");

const plano = (x) => (x || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const coincide = (s, q) => !q || plano([s.datos.nombre, s.patient?.nombre, s.datos.telefono, s.datos.email, s.datos.tutor, s.cuestionario_titulo].join(" ")).includes(plano(q.trim()));

const CAMPOS = { telefono: "teléfono", email: "email", fecha_nacimiento: "fecha de nacimiento", tutor: "padre/madre/tutor", motivo_consulta: "motivo", colegio: "colegio", curso: "curso", centro: "centro", quien_remite: "quién remite", edad: "edad", dni: "DNI", direccion: "dirección", codigo_postal: "código postal", poblacion: "población", telefono2: "segundo móvil", telefono_fijo: "teléfono fijo", telefono_trabajo: "teléfono trabajo", profesion: "profesión", nombre_padre: "padre", profesion_padre: "profesión padre", nombre_madre: "madre", profesion_madre: "profesión madre", profesion_tutor: "profesión tutor", desde_cuando: "desde cuándo", conoce_oc: "conoce O. Comportamental" };

const Solicitud = ({ s, onDone }) => {
  const otroNombre = s.patient && s.datos.nombre && norm(s.datos.nombre) !== norm(s.patient.nombre);
  const separar = async () => {
    if (!window.confirm(`¿Crear una ficha nueva para «${s.datos.nombre}» y pasarle su cuestionario? Se quitará de la ficha de ${s.patient.nombre}.`)) return;
    try {
      const { data } = await api.post(`/solicitudes-web/${s.id}/separar`);
      toast.success(data.rellenados_antes.length ? `Ficha nueva creada. Revisa en ${s.patient.nombre} estos datos que se copiaron: ${data.rellenados_antes.map((k) => CAMPOS[k] || k).join(", ")}` : "Ficha nueva creada", { duration: 12000 });
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const vincular = async (p) => {
    const distinto = norm(p.nombre) !== norm(s.datos.nombre || "");
    if (!window.confirm(`${distinto ? `¡Ojo! El nombre es distinto («${s.datos.nombre}» y «${p.nombre}»): si es un hermano NO lo unas.\n\n` : ""}¿Es el mismo paciente que ${p.nombre}? Se pasarán los datos y el cuestionario a su ficha${s.accion === "creado" ? " y se borrará la ficha nueva" : ""}.`)) return;
    try {
      await api.post(`/solicitudes-web/${s.id}/vincular`, { patient_id: p.id });
      toast.success(`Vinculado con ${p.nombre}`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const revisada = async () => {
    try {
      await api.post(`/solicitudes-web/${s.id}/revisada`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`solicitud-${s.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${s.accion === "creado" ? "bg-emerald-50 text-emerald-800" : "bg-blue-50 text-blue-800"}`}>{s.accion === "creado" ? "Paciente nuevo creado" : "Paciente ya existía"}</span>
        <span className="text-xs text-slate-500">{fmtDate(s.fecha)} · {s.cuestionario_titulo || (s.tipo === "contacto" ? "Formulario de contacto" : s.tipo)}</span>
        {s.patient ? <Link to={`/pacientes/${s.patient.id}`} className="font-medium text-[#1E3A8A] hover:underline" data-testid={`solicitud-paciente-${s.id}`}>{s.patient.nombre}</Link> : <span className="text-slate-400">{s.datos.nombre}</span>}
        {!s.revisada && <button onClick={revisada} data-testid={`solicitud-revisada-${s.id}`} className="ml-auto inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1 text-xs hover:bg-slate-50"><CheckCircle2 className="h-3.5 w-3.5" />Revisada</button>}
      </div>
      {otroNombre && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs text-rose-900" data-testid={`solicitud-otro-nombre-${s.id}`}>
          <span>En la web se llama <b>{s.datos.nombre}</b> pero está en la ficha de <b>{s.patient.nombre}</b>.</span>
          <button onClick={separar} data-testid={`solicitud-separar-${s.id}`} className="rounded bg-rose-700 px-2 py-0.5 text-white">Es otra persona: crear su ficha</button>
        </div>
      )}
      <div className="text-xs text-slate-600 mt-2">
        {[s.datos.telefono, s.datos.email, s.datos.tutor && `Tutor: ${s.datos.tutor}`].filter(Boolean).join(" · ")}
        {s.rellenados?.length > 0 && <span className="text-slate-400"> · Rellenado: {s.rellenados.map((k) => CAMPOS[k] || k).join(", ")}</span>}
      </div>
      {s.datos.mensaje && <p className="text-sm text-slate-700 mt-2 whitespace-pre-line">{s.datos.mensaje}</p>}
      {s.posibles?.length > 0 && (
        <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid={`solicitud-posibles-${s.id}`}>
          <div className="flex items-center gap-1 font-semibold"><AlertTriangle className="h-3.5 w-3.5" />¿Es alguno de estos pacientes? (puede ser un hermano con el mismo teléfono)</div>
          {s.posibles.map((p) => (
            <div key={p.id} className="flex items-center gap-2 mt-1">
              <Link to={`/pacientes/${p.id}`} className="underline">{p.nombre}</Link><span className="text-amber-700">({p.motivo})</span>
              <button onClick={() => vincular(p)} data-testid={`solicitud-vincular-${p.id}`} className="rounded border border-amber-400 bg-white px-2 py-0.5">Es el mismo</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RellenarFichas = () => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/cuestionarios/rellenar-fichas");
      toast.success(data.fichas ? `Completadas ${data.fichas} fichas (${data.campos} datos) desde sus cuestionarios` : "No había datos nuevos que completar");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return <button onClick={run} disabled={busy} data-testid="rellenar-fichas-btn" className="rounded-md border border-[#1E3A8A] bg-white px-3 py-2 text-sm text-[#1E3A8A] hover:bg-slate-50">{busy ? "Completando…" : "Completar fichas con los cuestionarios"}</button>;
};

export default function SolicitudesWeb() {
  const isAdmin = useAuth().user?.role === "admin";
  const [todas, setTodas] = useState(false);
  const [rows, setRows] = useState(null);
  const [tick, setTick] = useState(0);
  const [q, setQ] = useState("");
  const buscando = q.trim().length > 0;
  useEffect(() => {
    api.get("/solicitudes-web", { params: { revisadas: todas || buscando } }).then((r) => setRows(r.data));
  }, [todas, tick, buscando]);
  const lista = rows?.filter((s) => coincide(s, q));
  return (
    <div data-testid="solicitudes-web-page">
      <PageTitle title="Solicitudes web" subtitle="Formularios y cuestionarios que llegan desde saludyvision.es">
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="solicitudes-search" placeholder="Buscar nombre, teléfono, email…" className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm w-64" />
        <button onClick={() => setTodas(!todas)} data-testid="solicitudes-toggle" className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">{todas ? "Ver solo sin revisar" : "Ver también revisadas"}</button>
        {isAdmin && <RellenarFichas />}
      </PageTitle>
      <div className="space-y-3">
        {rows === null && <div className="text-slate-400">Cargando…</div>}
        {buscando && lista && <div className="text-xs text-slate-500" data-testid="solicitudes-search-count">{lista.length} resultado{lista.length === 1 ? "" : "s"} (incluye revisadas)</div>}
        {lista?.length === 0 && <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-400" data-testid="solicitudes-empty"><Globe className="h-6 w-6 mx-auto mb-2" />{buscando ? "Ninguna solicitud coincide." : "No hay solicitudes nuevas."}</div>}
        {lista?.map((s) => <Solicitud key={s.id} s={s} onDone={() => setTick(tick + 1)} />)}
      </div>
    </div>
  );
}
